import React, { useEffect, useState } from "react";

import { LoadingIcon } from "@packages/ds";
import { useIntl } from "react-intl";

import { useNotification } from "Containers/Notifications";
import Button from "ds/Button";
import * as Sticker from "ds/Sticker";
import Tag from "ds/Tag";
import useDecodedParams from "Hooks/useDecodedParams";
import { useTrialInfo } from "Reducers/organization/hooks/useTrialnfo";
import {
  updateSubscription,
  errorsSelector,
  loadingSelector
} from "Reducers/organization/subscription";
import { SellableModalContentProps } from "src/organization/common/components/sellableBanners/types";
import { useAppDispatch, useAppSelector } from "Store/hooks";

import * as S from "./ProfilingSellable.styles";

import type { SubscriptionEstimateComplex } from "@packages/client";

const continuous_profiling = "UPSUN-FEATURE-CONTINUOUS-PROFILING";

const ProfilingSellableModalContent = (props: SellableModalContentProps) => {
  const intl = useIntl();
  const notify = useNotification();
  const dispatch = useAppDispatch();
  const { organizationId } = useDecodedParams<{
    organizationId: string;
  }>();
  const { subscription, onClose, freeOnTrial } = props;
  const canEdit = subscription?.hasLink?.("update");
  const error: any = useAppSelector(state => errorsSelector(state));
  const isLoadingSubscription =
    useAppSelector(loadingSelector, () => false) === true;

  const [{ message, estimate }, setEstimate] = useState<{
    estimate?: SubscriptionEstimateComplex;
    message?: string;
  }>({});

  const errorMessage = message || error?.title;

  useEffect(() => {
    const fetchEstimate = async () => {
      if (!subscription?.plan) return;
      try {
        const { plan, user_licenses, environments, storage } = subscription;
        const estimate = await subscription.getEstimate({
          continuous_profiling,
          plan,
          user_licenses,
          environments,
          storage,
          format: "complex"
        });
        setEstimate({ estimate });
      } catch (error: any) {
        setEstimate({ message: error.title });
      }
    };
    fetchEstimate();
  }, [subscription]);

  const handleUpdate = () => {
    subscription &&
      dispatch(
        updateSubscription({
          updates: {
            continuous_profiling
          },
          subscription,
          organizationId
        })
      ).then(({ payload }) => {
        onClose?.(false);
        const sellable = payload?.continuous_profiling;
        sellable &&
          notify.success({
            title: intl.formatMessage({
              id: "add_on.continous_profiling_enabled"
            }),
            description: intl.formatMessage({ id: "add_on.success_message" })
          });
      });
  };

  const cost = estimate?.options.continuous_profiling;
  const estimateIsLoaded = estimate || message;

  const { isTrial } = useTrialInfo();
  const showTrialBanner = freeOnTrial && isTrial;

  return (
    <S.BannerContent>
      {showTrialBanner && (
        <Tag variant="rose">
          {intl.formatMessage({ id: "free_during_trial" })}
        </Tag>
      )}
      <S.ModalTitle>
        {intl.formatMessage({ id: "add_on.continous_profiling_add_on" })}
      </S.ModalTitle>
      <S.ModelDescription>
        {intl.formatMessage({
          id: "add_on.continous_profiling_enable_description"
        })}
      </S.ModelDescription>
      <S.Cost>{cost && `${cost?.formatted} /month`}</S.Cost>
      {errorMessage && (
        <Sticker.Root priority="critical">
          <Sticker.Icon priority="critical" />
          <Sticker.Text>{errorMessage}</Sticker.Text>
        </Sticker.Root>
      )}
      <S.ButtonGroup>
        <Button
          analyticId="cancel"
          onClick={() => onClose?.(false)}
          disabled={isLoadingSubscription}
          variant="secondary"
        >
          {intl.formatMessage({ id: "cancel" })}
        </Button>
        {isLoadingSubscription || !estimateIsLoaded ? (
          <LoadingIcon />
        ) : (
          <Button
            disabled={!canEdit}
            analyticId="observability-enable"
            onClick={handleUpdate}
          >
            {intl.formatMessage({ id: "enable" })}
          </Button>
        )}
      </S.ButtonGroup>
    </S.BannerContent>
  );
};

export default ProfilingSellableModalContent;
